// Aplikace: úvodní obrazovka, stůl (pozvánka, presence, výběr hry), hra a hlášení stavu spojení.
import type { GameEvent } from "../games/types.ts";
import type { GameSnapshot, RoomSnapshot, ServerMessage } from "../shared/protocol.ts";
import { announce, announceAlways, initAnnouncer, setAnnouncerEnabled } from "./announce.ts";
import { play, setMuted, setVolume, unlockAudio } from "./audio.ts";
import { CLIENT_GAMES } from "./games/registry.ts";
import type { ClientGame } from "./games/types.ts";
import { Connection, type ConnStatus } from "./net.ts";
import { hasAudio, store, touchReader, usesKeyboard, type Mode } from "./store.ts";

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

const MODES: { value: Mode; label: string }[] = [
  { value: "zvuk", label: "Zvuk: hraní poslechem a klávesnicí, se čtečkou obrazovky" },
  { value: "obraz", label: "Obraz: obrázky a dotyk, bez zvuku" },
  { value: "oboje", label: "Zvuk i obraz" },
  { value: "dotyk", label: "Dotyk se čtečkou (VoiceOver na iPhonu): karty jsou tlačítka, zvuky a hlášení" },
];

const state = {
  mode: (store.getMode() ?? "oboje") as Mode,
  name: store.getName(),
  roomCode: readRoomFromHash(),
  me: "",
  room: null as RoomSnapshot | null,
  game: null as ClientGame | null,
  gameId: "",
  gameOver: false,
  conn: null as Connection | null,
  lastStatus: "idle" as ConnStatus | "idle",
};

function readRoomFromHash(): string | null {
  const m = location.hash.match(/stul=([A-Za-z0-9_-]{22})/);
  return m ? m[1] : null;
}

function inviteUrl(code: string) {
  return `${location.origin}/#stul=${code}`;
}

// ---------- Obrazovky ----------

type Screen = "start" | "lobby" | "game" | "message";
let currentScreen: Screen = "start";

function show(screen: Screen, focusEl?: HTMLElement) {
  const changed = currentScreen !== screen;
  currentScreen = screen;
  for (const s of ["start", "lobby", "game", "message"]) $(`screen-${s}`).hidden = s !== screen;
  // Nová obrazovka začíná nahoře (jinak by zůstala odscrollovaná z dlouhého stolu).
  if (changed) window.scrollTo(0, 0);
  if (changed || focusEl) (focusEl ?? document.querySelector<HTMLElement>(`#screen-${screen} h1`))?.focus({ preventScroll: true });
}

// ---------- Nastavení (režim, hlasitost) ----------

function modeRadios(container: HTMLElement, prefix: string) {
  for (const m of MODES) {
    const id = `${prefix}-mode-${m.value}`;
    const wrap = document.createElement("div");
    wrap.className = "radio";
    wrap.innerHTML = `<input type="radio" name="${prefix}-mode" id="${id}" value="${m.value}"><label for="${id}"></label>`;
    wrap.querySelector("label")!.textContent = m.label;
    const input = wrap.querySelector("input")!;
    input.checked = state.mode === m.value;
    input.addEventListener("change", () => input.checked && applyMode(m.value));
    container.appendChild(wrap);
  }
}

function settingsPanel(container: HTMLElement, prefix: string) {
  const fs = document.createElement("fieldset");
  fs.className = "mode-fieldset";
  fs.innerHTML = "<legend>Režim</legend>";
  modeRadios(fs, prefix);
  container.appendChild(fs);

  const p = document.createElement("p");
  p.className = "field";
  p.innerHTML = `<label for="${prefix}-volume">Hlasitost</label><input id="${prefix}-volume" type="range" min="0" max="100" step="5">`;
  const range = p.querySelector("input")!;
  range.value = String(Math.round(store.getVolume() * 100));
  range.addEventListener("input", () => {
    const v = Number(range.value) / 100;
    store.setVolume(v);
    setVolume(v);
    syncVolume(range);
  });
  container.appendChild(p);

  const test = document.createElement("button");
  test.type = "button";
  test.textContent = "Vyzkoušet zvuk";
  test.addEventListener("click", () => {
    unlockAudio();
    play("tvuj-tah");
  });
  container.appendChild(test);
}

function syncVolume(source: HTMLInputElement) {
  for (const r of document.querySelectorAll<HTMLInputElement>('input[type="range"]')) if (r !== source) r.value = source.value;
}

function applyMode(mode: Mode) {
  state.mode = mode;
  store.setMode(mode);
  for (const r of document.querySelectorAll<HTMLInputElement>(`input[value="${mode}"]`)) r.checked = true;
  setMuted(!hasAudio(mode));
  setAnnouncerEnabled(hasAudio(mode));
  document.body.dataset.mode = mode;
  configureBoard();
  state.game?.setMode(mode);
}

function configureBoard() {
  const board = $("board");
  const label = CLIENT_GAMES[state.gameId]?.boardLabel ?? "Hrací plocha";
  if (usesKeyboard(state.mode)) {
    board.setAttribute("role", "application");
    board.setAttribute("aria-label", label);
    board.tabIndex = 0;
  } else {
    board.removeAttribute("role");
    board.removeAttribute("aria-label");
    board.removeAttribute("tabindex");
  }
}

// ---------- Spojení ----------

const CONN_TEXT: Record<ConnStatus, string> = {
  connecting: "Připojuji…",
  open: "Připojeno",
  reconnecting: "Spojení přerušeno, připojuji znovu…",
  closed: "Odpojeno",
};

function onStatus(s: ConnStatus) {
  const prev = state.lastStatus;
  state.lastStatus = s;
  const el = $("conn");
  el.dataset.state = s;
  el.textContent = CONN_TEXT[s];
  if (s === "reconnecting" && prev !== "reconnecting") announceAlways("Spojení přerušeno, připojuji znovu.");
  if (s === "open" && prev === "reconnecting") announceAlways("Znovu připojeno.");
}

function connect(create: boolean) {
  state.conn?.stop();
  let creating = create;
  const conn = new Connection({
    onOpen: (send) => {
      const room = state.room?.code ?? state.roomCode ?? undefined;
      send({
        t: "hello",
        name: state.name,
        room: creating ? undefined : room,
        token: room ? store.getToken(room) ?? undefined : undefined,
        create: creating && !room,
      });
    },
    onMessage: (m) => {
      if (m.t === "welcome") creating = false;
      handle(m);
    },
    onStatus,
  });
  state.conn = conn;
  (window as unknown as { __ha: object }).__ha = { drop: () => conn.simulateDrop() };
  conn.start();
}

function handle(m: ServerMessage) {
  switch (m.t) {
    case "welcome": {
      state.me = m.you;
      state.roomCode = m.room.code;
      store.setToken(m.room.code, m.token);
      history.replaceState(null, "", `#stul=${m.room.code}`);
      const first = !state.room;
      onRoom(m.room, first);
      if (m.game) onGame(m.game, [], true);
      else if (first || currentScreen !== "lobby") showLobby(first);
      break;
    }
    case "room":
      onRoom(m.room, false);
      break;
    case "game":
      onGame(m.game, m.events, false);
      break;
    case "error":
      onError(m.code, m.message);
      break;
  }
}

function onError(code: string, message: string) {
  if (code === "room-not-found" || code === "room-full") {
    if (state.roomCode) store.setToken(state.roomCode, null);
    state.conn?.stop();
    state.room = null;
    $("message-title").textContent = code === "room-full" ? "Stůl je plný" : "Stůl nenalezen";
    $("message-text").textContent = message;
    show("message");
    announceAlways(message);
    return;
  }
  if (code === "action-rejected") {
    state.game?.rejected(message);
    return;
  }
  const target = currentScreen === "start" ? $("start-error") : $("lobby-error");
  target.textContent = message;
}

// ---------- Stůl ----------

function onRoom(room: RoomSnapshot, first: boolean) {
  const prev = state.room;
  state.room = room;
  if (prev && !first) presenceChanges(prev, room);
  renderLobby(room);
  if (room.phase === "lobby" && (currentScreen === "game" || state.game)) {
    destroyGame();
    showLobby(false);
  }
}

function presenceChanges(prev: RoomSnapshot, room: RoomSnapshot) {
  for (const p of room.players) {
    if (p.id === state.me) continue;
    const before = prev.players.find((x) => x.id === p.id);
    if ((!before || !before.connected) && p.connected) {
      play("pripojeni");
      announce(`${p.name} je u stolu.`);
    } else if (before?.connected && !p.connected) {
      play("odpojeni");
      announce(`${p.name}: spojení přerušeno.`);
    }
  }
}

function renderLobby(room: RoomSnapshot) {
  const isHost = room.hostId === state.me;
  $<HTMLInputElement>("invite-link").value = inviteUrl(room.code);

  const ul = $("players");
  ul.textContent = "";
  for (const p of room.players) {
    const li = document.createElement("li");
    li.className = p.connected ? "online" : "offline";
    const tags = [p.isHost ? "hostitel" : "", p.id === state.me ? "ty" : "", p.connected ? "připojeno" : "odpojeno"].filter(Boolean);
    li.textContent = `${p.name} (${tags.join(", ")})`;
    ul.appendChild(li);
  }

  const game = room.games.find((g) => g.id === room.selected.gameId);
  $("game-seating").textContent = game ? seatingText(room, game) : "";
  $("game-form").hidden = !isHost;
  $("game-wait").hidden = isHost;
  $("btn-end-game").hidden = !isHost;
  if (!isHost) {
    const host = room.players.find((p) => p.isHost)?.name ?? "hostitel";
    const opts = game?.options.map((o) => o.choices.find((c) => c.value === room.selected.options[o.key])?.label).filter(Boolean) ?? [];
    $("game-wait").textContent = `Hru vybírá ${host}. Zatím vybráno: ${game?.name ?? "nic"}${opts.length ? ", " + opts.join(", ") : ""}. Čeká se na začátek hry.`;
    return;
  }
  renderGamePicker(room);
}

function playersRange(min: number, max: number): string {
  const word = max >= 5 ? "hráčů" : "hráči";
  return min === max ? `${max} ${word}` : `${min} až ${max} ${word}`;
}

/** Kdo bude hrát, když hostitel teď spustí hru (stejné pořadí jako na serveru: hostitel první). */
function seatingText(room: RoomSnapshot, game: RoomSnapshot["games"][number]): string {
  const online = room.players.filter((p) => p.connected).sort((a, b) => Number(b.isHost) - Number(a.isHost));
  const seated = online.slice(0, game.maxPlayers);
  // Hra s roboty doplní prázdná místa do počtu z volby „Hráčů celkem“ (stejně jako server).
  const seats = game.bots ? Math.min(game.maxPlayers, Math.max(seated.length, Number(room.selected.options.seats) || seated.length)) : seated.length;
  const bots = seats - seated.length;
  if (seats < game.minPlayers) {
    return `${game.name} potřebuje aspoň ${game.minPlayers} hráče. Pošli odkaz dalším.`;
  }
  const rest = online.slice(game.maxPlayers);
  const names = seated.map((p) => (p.id === state.me ? `${p.name} (ty)` : p.name));
  const botText = bots === 1 ? "1 robot" : `${bots} roboti`;
  let text = `Hrát bude: ${names.join(", ")}${bots ? ` a ${botText}` : ""}.`;
  if (rest.length) text += ` ${game.name} unese nejvýš ${game.maxPlayers} hráčů, ${rest.map((p) => p.name).join(", ")} se bude dívat.`;
  return text;
}

function renderGamePicker(room: RoomSnapshot) {
  // Nepřekreslujeme, pokud se nic nezměnilo – jinak by se ztratil fokus z přepínače.
  const key = JSON.stringify([room.games.map((g) => g.id), room.selected]);
  const form = $("game-form");
  if (form.dataset.key === key) return;
  form.dataset.key = key;

  const choice = $("game-choice");
  choice.querySelectorAll(".radio").forEach((n) => n.remove());
  for (const g of room.games) choice.appendChild(radio("game", g.id, `${g.name}, ${playersRange(g.minPlayers, g.maxPlayers)}`, g.id === room.selected.gameId, () => sendSelect(g.id, {})));

  const opts = $("game-options");
  opts.textContent = "";
  const game = room.games.find((g) => g.id === room.selected.gameId);
  for (const o of game?.options ?? []) {
    const fs = document.createElement("fieldset");
    const legend = document.createElement("legend");
    legend.textContent = o.label;
    fs.appendChild(legend);
    for (const c of o.choices) {
      fs.appendChild(
        radio(`opt-${o.key}`, c.value, c.label, room.selected.options[o.key] === c.value, () =>
          sendSelect(room.selected.gameId, { ...room.selected.options, [o.key]: c.value }),
        ),
      );
    }
    opts.appendChild(fs);
  }
}

function radio(name: string, value: string, label: string, checked: boolean, onChange: () => void) {
  const id = `${name}-${value}`;
  const wrap = document.createElement("div");
  wrap.className = "radio";
  wrap.innerHTML = `<input type="radio" name="${name}" id="${id}"><label for="${id}"></label>`;
  wrap.querySelector("label")!.textContent = label;
  const input = wrap.querySelector("input")!;
  input.value = value;
  input.checked = checked;
  input.addEventListener("change", () => input.checked && onChange());
  return wrap;
}

function sendSelect(gameId: string, options: Record<string, string>) {
  state.conn?.send({ t: "select", gameId, options });
}

function showLobby(first: boolean) {
  $("lobby-error").textContent = "";
  show("lobby");
  if (first) {
    const isHost = state.room?.hostId === state.me;
    announce(isHost ? "Stůl je připravený. Pošli odkaz druhému hráči." : "Jsi u stolu.");
  }
}

// ---------- Hra ----------

function onGame(snap: GameSnapshot, events: GameEvent[], initial: boolean) {
  const module = CLIENT_GAMES[snap.gameId];
  if (!module) return;
  let mounted = false;
  if (!state.game || state.gameId !== snap.gameId) {
    destroyGame();
    state.gameId = snap.gameId;
    $("game-title").textContent = state.room?.games.find((g) => g.id === snap.gameId)?.name ?? "Hra";
    configureBoard();
    state.game = module.mount(
      {
        me: state.me,
        board: $("board"),
        status: $("game-status"),
        sendAction: (action) => state.conn?.send({ t: "action", action }),
      },
      state.mode,
    );
    mounted = true;
  }
  state.game.update(snap.view, events, initial);
  const spectator = !(state.room?.seated ?? []).includes(state.me);
  $("game-spectator").hidden = !spectator;
  if (mounted) {
    show("game", usesKeyboard(state.mode) ? $("board") : $("game-title"));
    if (spectator) announceAlways("Hra už běží, díváš se. Do další hry se zapojíš automaticky.");
  }
  renderGameOver(snap.over, !initial && events.some((e) => e.type === "gameOver"));
}

function renderGameOver(over: boolean, justEnded: boolean) {
  state.gameOver = over;
  const box = $("game-over");
  box.hidden = !over;
  if (!over) return;
  const isHost = state.room?.hostId === state.me;
  $("game-result").textContent = state.game?.resultText() ?? "";
  $("btn-to-lobby").hidden = !isHost;
  $("game-over-wait").hidden = isHost;
  if (justEnded) {
    if (usesKeyboard(state.mode)) announce(isHost ? "Tabulátorem se dostaneš na tlačítko Zpět ke stolu." : "");
    else if (touchReader(state.mode)) announce(isHost ? "Pod hrací plochou je tlačítko Zpět ke stolu." : "");
    else box.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}

function destroyGame() {
  state.game?.destroy();
  state.game = null;
  state.gameId = "";
  $("board").textContent = "";
  $("game-status").textContent = "";
  $("game-over").hidden = true;
}

// ---------- Start ----------

function init() {
  initAnnouncer();
  modeRadios($("start-mode"), "start");
  settingsPanel($("lobby-settings"), "lobby");
  settingsPanel($("game-settings"), "game");
  applyMode(state.mode);
  setVolume(store.getVolume());

  const nameInput = $<HTMLInputElement>("name");
  nameInput.value = state.name;
  if (state.roomCode) {
    $("start-intro").textContent = "Někdo tě zve ke stolu. Napiš své jméno a stiskni Hrát.";
  }

  $("start-form").addEventListener("submit", (e) => {
    e.preventDefault();
    const name = nameInput.value.trim();
    if (!name) {
      $("start-error").textContent = "Napiš prosím své jméno.";
      nameInput.focus();
      return;
    }
    $("start-error").textContent = "";
    state.name = name;
    store.setName(name);
    unlockAudio();
    connect(!state.roomCode);
  });

  $("btn-copy").addEventListener("click", async () => {
    const input = $<HTMLInputElement>("invite-link");
    try {
      await navigator.clipboard.writeText(input.value);
      announceAlways("Odkaz zkopírován.");
    } catch {
      input.select();
      announceAlways("Kopírování se nepovedlo. Odkaz je vybraný, zkopíruj ho ručně.");
    }
  });
  if (typeof navigator.share === "function") {
    const share = $("btn-share");
    share.hidden = false;
    share.addEventListener("click", () => {
      navigator.share({ title: "Pojď hrát", url: $<HTMLInputElement>("invite-link").value }).catch(() => {});
    });
  }

  $("game-form").addEventListener("submit", (e) => {
    e.preventDefault();
    $("lobby-error").textContent = "";
    state.conn?.send({ t: "start" });
  });
  $("btn-to-lobby").addEventListener("click", () => state.conn?.send({ t: "toLobby" }));
  $("btn-end-game").addEventListener("click", () => state.conn?.send({ t: "toLobby" }));
  $("btn-new-table").addEventListener("click", () => {
    state.roomCode = null;
    state.room = null;
    history.replaceState(null, "", "/");
    show("start");
    $("start-intro").textContent = "Napiš své jméno a stiskni Hrát. Vytvoří se nový stůl.";
  });

  window.addEventListener("hashchange", () => {
    const code = readRoomFromHash();
    if (code && code !== state.room?.code) location.reload();
  });

  // Zvuk se odemkne prvním dotykem nebo klávesou kdekoli (pro případ, že se stránka
  // po návratu znovu načetla a hráč se připojil bez tlačítka Hrát).
  for (const ev of ["touchend", "click", "keydown"]) document.addEventListener(ev, unlockAudio, { capture: true, passive: true });

  show("start");
  if (state.roomCode && state.name && store.getToken(state.roomCode)) {
    // Safari stránku po zamčení telefonu někdy znovu načte: vrátíme se ke stolu bez zásahu.
    $("start-intro").textContent = "Vracím tě ke stolu…";
    connect(false);
    return;
  }
  if (!nameInput.value) nameInput.focus();
  else $("btn-play").focus();
}

init();
